let audioContext = null;

const getAudioContext = () => {
  if (typeof window === 'undefined') return null;

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  if (!audioContext) {
    audioContext = new AudioContextClass();
  }

  return audioContext;
};

/**
 * Browsers require a user interaction before notification audio can play.
 * Calling this once registers lightweight unlock handlers for that interaction.
 */
export const initializeChatNotificationSound = () => {
  if (typeof window === 'undefined') return () => {};

  const unlockAudio = () => {
    const context = getAudioContext();
    if (context?.state === 'suspended') {
      void context.resume();
    }
  };

  const events = ['pointerdown', 'keydown', 'touchstart'];
  events.forEach(eventName => window.addEventListener(eventName, unlockAudio, { passive: true }));

  return () => {
    events.forEach(eventName => window.removeEventListener(eventName, unlockAudio));
  };
};

/**
 * Play a short two-tone bell without relying on an environment-specific file.
 */
export const playChatNotificationSound = async () => {
  const context = getAudioContext();
  if (!context) return;

  try {
    if (context.state === 'suspended') {
      await context.resume();
    }
    if (context.state !== 'running') return;

    const startTime = context.currentTime;
    const masterGain = context.createGain();
    masterGain.gain.setValueAtTime(0.55, startTime);
    masterGain.connect(context.destination);

    [
      { frequency: 523.25, delay: 0, duration: 0.58, volume: 0.055 },
      { frequency: 659.25, delay: 0.2, duration: 0.68, volume: 0.04 }
    ].forEach(({ frequency, delay, duration, volume }) => {
      const oscillator = context.createOscillator();
      const toneGain = context.createGain();
      const toneStart = startTime + delay;
      const toneEnd = toneStart + duration;

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, toneStart);
      toneGain.gain.setValueAtTime(0.0001, toneStart);
      toneGain.gain.linearRampToValueAtTime(volume, toneStart + 0.06);
      toneGain.gain.exponentialRampToValueAtTime(0.0001, toneEnd);

      oscillator.connect(toneGain);
      toneGain.connect(masterGain);
      oscillator.start(toneStart);
      oscillator.stop(toneEnd);
    });
  } catch (error) {
    // Audio can remain blocked until the browser receives a user interaction.
  }
};
